import { useState } from 'react';
import type { Tenant } from '../../lib/database.types';
import {
  CRM_STAGES,
  CRM_STAGE_LABELS,
  crmTone,
  toCrmInfo,
  isPlausibleEmail,
  type CrmInfo,
  type CrmStage,
  type CrmTone,
} from '../../lib/crm';
import { saveCrm } from '../../lib/crmService';

/**
 * Section « CRM réseau » du SuperAdmin (brique B) : une fiche éditable par resto client
 * (contact gérant, date d'ouverture, statut commercial, notes), enregistrée via RPC sécurisée.
 */

const TONE_STYLE: Record<CrmTone, string> = {
  good: 'bg-green-100 text-green-800',
  warn: 'bg-amber-100 text-amber-800',
  bad: 'bg-red-100 text-red-800',
  neutral: 'bg-slate-100 text-slate-600',
};

export default function CrmSection({ tenants }: { tenants: Tenant[] }) {
  return (
    <div className="max-w-3xl">
      <h1 className="text-xl font-bold mb-1">CRM réseau (tous clients)</h1>
      <p className="text-sm opacity-70 mb-4">
        Fiche commerciale de chaque restaurant partenaire. {tenants.length} client(s).
      </p>
      <div className="flex flex-col gap-3">
        {tenants.map((t) => (
          <CrmCard key={t.id} tenant={t} />
        ))}
      </div>
    </div>
  );
}

export function CrmCard({ tenant }: { tenant: Tenant }) {
  const [info, setInfo] = useState<CrmInfo>(toCrmInfo(tenant));
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const set = <K extends keyof CrmInfo>(key: K, value: CrmInfo[K]) => {
    setInfo((prev) => ({ ...prev, [key]: value }));
    setFeedback(null);
  };

  const emailOk = isPlausibleEmail(info.managerEmail);

  const save = async () => {
    setSaving(true);
    setFeedback(null);
    const res = await saveCrm(tenant.id, info);
    setSaving(false);
    setFeedback(res.ok ? '✅ Fiche enregistrée.' : '⚠️ ' + res.error);
  };

  return (
    <div className="rounded-lg bg-white border border-slate-200 p-4">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <span className="font-semibold">
          {tenant.name} <span className="opacity-50 text-sm">/{tenant.slug}</span>
        </span>
        <span className={'text-xs rounded-full px-2 py-1 ' + TONE_STYLE[crmTone(info.stage)]}>
          {CRM_STAGE_LABELS[info.stage]}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <Field label="Gérant">
          <input
            value={info.managerName}
            onChange={(e) => set('managerName', e.target.value)}
            placeholder="Nom du gérant"
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label="Téléphone">
          <input
            value={info.managerPhone}
            onChange={(e) => set('managerPhone', e.target.value)}
            placeholder="06 12 34 56 78"
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label="Email">
          <input
            value={info.managerEmail}
            onChange={(e) => set('managerEmail', e.target.value)}
            placeholder="gerant@resto.fr"
            className={
              'w-full rounded border px-2 py-1.5 text-sm ' +
              (emailOk ? 'border-slate-300' : 'border-red-400')
            }
          />
        </Field>
        <Field label="Date d'ouverture">
          <input
            type="date"
            value={info.openedOn}
            onChange={(e) => set('openedOn', e.target.value)}
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          />
        </Field>
        <Field label="Statut commercial">
          <select
            value={info.stage}
            onChange={(e) => set('stage', e.target.value as CrmStage)}
            className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
          >
            {CRM_STAGES.map((s) => (
              <option key={s} value={s}>
                {CRM_STAGE_LABELS[s]}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Notes">
        <textarea
          value={info.notes}
          onChange={(e) => set('notes', e.target.value)}
          rows={2}
          placeholder="Historique, négociation, points d'attention…"
          className="w-full rounded border border-slate-300 px-2 py-1.5 text-sm"
        />
      </Field>

      <div className="flex items-center gap-3 mt-3">
        <button
          type="button"
          disabled={saving || !emailOk}
          onClick={save}
          className="rounded-md px-4 py-2 text-white font-semibold disabled:opacity-40"
          style={{ background: '#2563EB' }}
        >
          {saving ? 'Enregistrement…' : 'Enregistrer la fiche'}
        </button>
        {!emailOk && <span className="text-xs text-red-600">Email invalide</span>}
        {feedback && <span className="text-sm">{feedback}</span>}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase opacity-60 mb-1">{label}</span>
      {children}
    </label>
  );
}
